<template>
  <pn-modal
    label="Edit VAT Price"
    sheet="true"
    max-width="500px"
    :open="open"
    @close="onClose"
  >
    <pn-toast
      v-if="errorMsg"
      temporary
      :text="errorMsg"
      appearance="error"
      @close="errorMsg = ''"
    ></pn-toast>
    <form class="edit-vat-price-form" @submit.prevent="handleUpdate">
      <pn-input
        label="Rate"
        name="rate"
        type="number"
        v-model="formValues.rate"
      ></pn-input>
      <pn-date-picker
        label-from="Effective From"
        :start="formValues.effectiveFrom"
        @dateSelection="selectDate($event, 'effectiveFrom')"
      ></pn-date-picker>
      <pn-date-picker
        label-from="Effective To"
        :start="formValues.effectiveTo"
        @dateSelection="selectDate($event, 'effectiveTo')"
      ></pn-date-picker>
      <div class="form-actions">
        <pn-button type="submit" :loading="isLoading">Update</pn-button>
        <pn-button
          appearance="light"
          variant="outlined"
          type="button"
          @click="onClose"
          >Cancel</pn-button
        >
      </div>
    </form>
  </pn-modal>
</template>

<script setup lang="ts">
import { reactive, computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import constant from "@/constant";
import { usePriceManagementStore } from "@/stores/priceManagement";
import type { PnDatePickerSelectEvent } from "@postnord/web-components/types/components";
import type { PriceManagementUpdateVatRequest } from "@/types/priceManagement";

const { PRICE_MANAGEMENT } = constant;

const props = defineProps<{
  open: boolean;
  priceListId: string | null;
  vatChargePriceId: string | null;
}>();

const emit = defineEmits<{
  (e: "onClose"): void;
  (e: "onClose", payload: { status: "success"; message: string }): void;
}>();

const priceManagementStore = usePriceManagementStore();
const { priceLists, isLoading } = storeToRefs(priceManagementStore);
const errorMsg = ref("");

const selectedVatChargePrice = computed(() => {
  const pricing = priceLists.value.find(
    (pl) => pl.priceList.id === props.priceListId,
  );
  return pricing?.vatChargePrice.find((v) => v.id === props.vatChargePriceId);
});

const toDateString = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const formValues = reactive({
  rate: "",
  effectiveFrom: "",
  effectiveTo: "",
});

const resetFormValues = () => {
  formValues.rate = selectedVatChargePrice.value?.vatRate ?? "";
  formValues.effectiveFrom = toDateString(
    selectedVatChargePrice.value?.effectiveFrom,
  );
  formValues.effectiveTo = toDateString(
    selectedVatChargePrice.value?.effectiveTo,
  );
};

watch(
  () => props.open,
  (now, prev) => {
    if (now === true && prev !== true) {
      errorMsg.value = "";
      resetFormValues();
    }
  },
);

const hasEmptyRequiredFields = (): boolean => {
  return (
    formValues.rate === "" ||
    Number.isNaN(Number(formValues.rate)) ||
    !formValues.effectiveFrom.trim()
  );
};

const handleUpdate = async () => {
  errorMsg.value = "";

  if (hasEmptyRequiredFields()) {
    errorMsg.value = "Rate and Effective From are required.";
    return;
  }

  if (!props.priceListId) {
    errorMsg.value = "Unable to find selected price list.";
    return;
  }

  const payload: PriceManagementUpdateVatRequest = {
    priceListId: props.priceListId,
    rate: Number(formValues.rate),
    effectiveFrom: formValues.effectiveFrom,
    effectiveTo: formValues.effectiveTo,
  };

  const updateResult = await priceManagementStore.updateVatPrice(payload);
  if (updateResult.status === "error") {
    errorMsg.value = updateResult.message;
    return;
  }

  emit("onClose", {
    status: "success",
    message: PRICE_MANAGEMENT.VAT_UPDATE_SUCCESS_MESSAGE,
  });
};

const onClose = () => {
  emit("onClose");
};

const selectDate = (
  event: CustomEvent<PnDatePickerSelectEvent>,
  field: "effectiveFrom" | "effectiveTo",
) => {
  const selectedDate = event.detail.start;
  if (selectedDate) {
    formValues[field] = selectedDate;
  }
};
</script>

<style scoped>
.edit-vat-price-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
}

.edit-vat-price-form :deep(pn-input),
.edit-vat-price-form :deep(pn-date-picker) {
  width: 400px;
}
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>
